import React, { useEffect, useState, Suspense, lazy } from 'react';
import Navbar from './components/Navbar';
import AnnouncementBar from './components/AnnouncementBar';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './components/pages/PageStyles.css';
import Home from './components/pages/Home';
import ScrollToTop from './components/ScrollToTop';
import AnalyticsTracker from './components/AnalyticsTracker';
import ErrorBoundary from './components/ErrorBoundary';
import WhatsAppWidget from './components/WhatsAppWidget';
import CalendarWidget from './components/CalendarWidget';
import MobileStickyCta from './components/MobileStickyCta';
import useHeroClearance from './hooks/useHeroClearance';
import useMobileStickyCta from './hooks/useMobileStickyCta';
import useContactModalIntent from './hooks/useContactModalIntent';
import NotFound from './components/pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import { SiteContentProvider } from './context/SiteContentContext';
import { ConnectionProvider, useConnection } from './context/ConnectionContext';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import UpdateBanner from './components/UpdateBanner';
import { MotionConfig } from 'framer-motion';
import countryPages from './content/countryPages.json';
import { COUNTRY_PAGE_PREFIX } from './utils/whatsapp';

// Everything below is route-only content — never needed until a visitor
// actually navigates there, so none of it belongs in the chunk every
// visitor downloads just to see the homepage. Grouped in the same
// priority order as the code-splitting task: admin portal (a), the 26
// static university pages (b), then the remaining heavy routes (c).

// -- (a) Admin portal — staff-only, and pulls in the heaviest single
// dependency in the app (react-quill's editor chrome, plus 20+ CRUD
// screens no visitor ever sees).
const AdminDashboard = lazy(() => import('./components/pages/AdminDashboard'));
const AdminLogin = lazy(() => import('./components/pages/AdminLogin'));
const GeneralSettings = lazy(() => import('./components/pages/GeneralSettings'));
const HomeHero = lazy(() => import('./components/pages/HomeHero'));
const HomePartners = lazy(() => import('./components/pages/HomePartners'));
const AboutStatsManager = lazy(() => import('./components/pages/AboutStatsManager'));
const SiteContentManager = lazy(() => import('./components/pages/SiteContentManager'));
const SuccessStoriesManager = lazy(() => import('./components/pages/SuccessStoriesManager'));
const BlogManager = lazy(() => import('./components/pages/BlogManager'));
const BlogCategoriesManager = lazy(() => import('./components/pages/BlogCategoriesManager'));
const BlogPostForm = lazy(() => import('./components/pages/BlogPostForm'));
const UniversitiesManager = lazy(() => import('./components/pages/UniversitiesManager'));
const UniversityForm = lazy(() => import('./components/pages/UniversityForm'));
const ProgramsManager = lazy(() => import('./components/pages/ProgramsManager'));
const ProgramForm = lazy(() => import('./components/pages/ProgramForm'));
const CountriesManager = lazy(() => import('./components/pages/CountriesManager'));
const CountryForm = lazy(() => import('./components/pages/CountryForm'));
const PricingManager = lazy(() => import('./components/pages/PricingManager'));
const ScholarshipsManager = lazy(() => import('./components/pages/ScholarshipsManager'));
const SEOManager = lazy(() => import('./components/pages/SEOManager'));
const TermsPrivacyManager = lazy(() => import('./components/pages/TermsPrivacyManager'));
const PricingPackageForm = lazy(() => import('./components/pages/PricingPackageForm'));
const RegistrationsManager = lazy(() => import('./components/pages/RegistrationsManager'));
const AnnouncementsManager = lazy(() => import('./components/pages/AnnouncementsManager'));
const AnnouncementForm = lazy(() => import('./components/pages/AnnouncementForm'));
const AnnouncementSubscriptionsManager = lazy(() => import('./components/pages/AnnouncementSubscriptionsManager'));
const CounsellorsManager = lazy(() => import('./components/pages/CounsellorsManager'));
const AdmissionResultsManager = lazy(() => import('./components/pages/AdmissionResultsManager'));
const CalendarManager = lazy(() => import('./components/pages/CalendarManager'));
const JobSearchInquiriesManager = lazy(() => import('./components/pages/JobSearchInquiriesManager'));
const EmergencySupportManager = lazy(() => import('./components/pages/EmergencySupportManager'));
const AlumniSignupsManager = lazy(() => import('./components/pages/AlumniSignupsManager'));
const EducationConsultationManager = lazy(() => import('./components/pages/EducationConsultationManager'));
const TourismConsultationManager = lazy(() => import('./components/pages/TourismConsultationManager'));


// -- (c) The rest of the site — real pages, but none of them are the
// homepage, so none of them need to be in its critical path.
const About = lazy(() => import('./components/pages/About'));
const ServicesPage = lazy(() => import('./components/pages/ServicesPage'));
const UniversitiesPage = lazy(() => import('./components/pages/UniversitiesPage'));
const UniversityDetail = lazy(() => import('./components/pages/UniversityDetail'));
const SearchResults = lazy(() => import('./components/pages/SearchResults'));
const ProgramSearch = lazy(() => import('./components/pages/ProgramSearch'));
const Scholarships = lazy(() => import('./components/Scholarships'));
const Destinations = lazy(() => import('./components/pages/Destinations'));
const SignUpPage = lazy(() => import('./components/pages/SignUpPage'));
const PartnerWithUs = lazy(() => import('./components/pages/PartnerWithUs'));
const Pricing = lazy(() => import('./components/pages/Pricing'));
const MyApplicationsPage = lazy(() => import('./components/pages/MyApplicationsPage'));
const BlogPage = lazy(() => import('./components/pages/Blog'));
const BlogPostDetail = lazy(() => import('./components/pages/BlogPostDetail'));
const StoryPage = lazy(() => import('./components/pages/StoryPage'));
const AllStories = lazy(() => import('./components/pages/AllStories'));
const AnnouncementsPage = lazy(() => import('./components/pages/Announcements'));
const AnnouncementDetail = lazy(() => import('./components/pages/AnnouncementDetail'));
const LegalPage = lazy(() => import('./components/pages/LegalPage'));
const SignDocumentPage = lazy(() => import('./components/pages/SignDocumentPage'));
const VerifyDocumentPage = lazy(() => import('./components/pages/VerifyDocumentPage'));
const FeedbackSurveyPage = lazy(() => import('./components/pages/FeedbackSurveyPage'));
const ConsentPage = lazy(() => import('./components/pages/ConsentPage'));
const DocumentTrackerPage = lazy(() => import('./components/pages/DocumentTrackerPage'));
const LessonsPage = lazy(() => import('./components/pages/LessonsPage'));
const LessonTestPage = lazy(() => import('./components/pages/LessonTestPage'));
const VisaDocumentsPage = lazy(() => import('./components/pages/VisaDocumentsPage'));
const ClientDocumentsPage = lazy(() => import('./components/pages/ClientDocumentsPage'));
const JobSearchSupportForm = lazy(() => import('./components/pages/JobSearchSupportForm'));
const WorkAbroadTrustPage = lazy(() => import('./components/pages/WorkAbroadTrustPage'));
const EmergencySupportForm = lazy(() => import('./components/pages/EmergencySupportForm'));
const AlumniSignupForm = lazy(() => import('./components/pages/AlumniSignupForm'));
const ConsultationBooking = lazy(() => import('./components/pages/ConsultationBooking'));
const EducationConsultationForm = lazy(() => import('./components/pages/EducationConsultationForm'));
const TourismConsultationForm = lazy(() => import('./components/pages/TourismConsultationForm'));
const MedicalTourism = lazy(() => import('./components/pages/MedicalTourism'));
const DynamicCountry = lazy(() => import('./components/countries/DynamicCountry'));
const CountryPage = lazy(() => import('./components/pages/CountryPage'));
const BecomeAnAgent = lazy(() => import('./components/pages/BecomeAnAgent'));

// Not homepage content — a popup that only ever appears once real intent
// signals are met (or never, for a returning visitor with a token / who's
// dismissed it before — see useContactModalIntent) — but it was previously
// a plain eager import, dragging react-icons/fa, @emailjs/browser and the
// full country-dial-code list into the initial bundle for something most
// visitors never even see.
const ContactInfoModal = lazy(() => import('./components/ContactInfoModal'));

// Wraps a lazy route element in its own Suspense boundary so each route
// gets a fallback sized to roughly match what it's replacing — a single
// shared boundary around the whole <Routes> tree would force one guessed
// height for every route, which is exactly what causes layout shift on
// the ones that guess wrong.
// Manage Site Content locked to the About page (admin sidebar: About – Page Content).
const AboutPageManager = () => <SiteContentManager fixedPage="about" />;

const withSuspense = (Component, minHeight = 500) => (
  <Suspense fallback={<LoadingSpinner minHeight={minHeight} label="Loading..." />}>
    <Component />
  </Suspense>
);

function App() {
  return (
    <ConnectionProvider>
      <AppShell />
    </ConnectionProvider>
  );
}

// useLocation() (inside useHeroClearance/useMobileStickyCta) needs a
// component that's a *descendant* of <Router>, not AppShell itself — AppShell
// renders <Router> as part of its own output, so it isn't inside that
// Router's context yet when its hooks run. Kept as its own component,
// rendered inside <Router> below, purely for that reason.
function FloatingActions() {
  const heroClear = useHeroClearance();
  const stickyCta = useMobileStickyCta();
  // The floating WhatsApp/Calendar bubble stack hides whenever the sticky
  // CTA bar is up too (its own WhatsApp action already covers that), not
  // just over the hero — otherwise mobile would show two floating
  // affordances stacked on each other.
  const quickActionsHidden = !heroClear || stickyCta.visible;

  return (
    <>
      {/* Floating WhatsApp Widget + the important-dates calendar stacked above
          it — wrapped in a landmark so this floating content (which sits
          outside <main> and <footer>) isn't orphaned from all page landmarks.
          Faded out (not unmounted) while it would overlap the mobile hero
          search card or double up with MobileStickyCta — see
          quickActionsHidden above. */}
      <div
        role="region"
        aria-label="Quick actions"
        className={`quick-actions${quickActionsHidden ? ' quick-actions--hidden' : ''}`}
      >
        <WhatsAppWidget />
        <CalendarWidget />
      </div>
      <MobileStickyCta visible={stickyCta.visible} dismiss={stickyCta.dismiss} />
    </>
  );
}

function AppShell() {
  const [waitingRegistration, setWaitingRegistration] = useState(null);
  const { isLite } = useConnection();
  // P11: the contact modal claims a single per-session interruption slot
  // (see useInterruptionGate) and never steals focus from a field the
  // visitor is actively typing into (checked live, at claim time, not just
  // at mount).
  const contactModal = useContactModalIntent();

  useEffect(() => {
    serviceWorkerRegistration.register({
      onUpdate: (registration) => setWaitingRegistration(registration),
    });
  }, []);

  return (
    <MotionConfig reducedMotion={isLite ? 'always' : 'never'}>
      <Router>
        <SiteContentProvider>
        <a className="skip-link" href="#main-content">Skip to main content</a>
        <ScrollToTop />
        <AnalyticsTracker />
        <AnnouncementBar />
        <Navbar />
        <main id="main-content" tabIndex={-1}>
        <ErrorBoundary>
          <Routes>
            <Route path="/" exact element={<Home />}/>
            <Route path="/about" element={withSuspense(About, 900)} />
            <Route path="/services" element={withSuspense(ServicesPage, 1400)} />
            <Route path="/universities" element={withSuspense(UniversitiesPage, 1200)} />
            <Route path="/universities/:slug" element={withSuspense(UniversityDetail, 1200)} />
            <Route path="/search-results" element={withSuspense(SearchResults, 900)} />
            <Route path="/programs" element={withSuspense(ProgramSearch, 1200)} />
            <Route path="/scholarships" element={withSuspense(Scholarships, 1400)} />
            <Route path="/sign-up" element={withSuspense(SignUpPage, 1400)} />
            <Route path="/partner-with-us" element={withSuspense(PartnerWithUs, 700)} />
            <Route path="/pricing" element={withSuspense(Pricing, 1200)} />
            <Route path="/my-applications" element={withSuspense(MyApplicationsPage, 700)} />
            <Route path="/blog" element={withSuspense(BlogPage, 900)} />
            <Route path="/blog/:slug" element={withSuspense(BlogPostDetail, 900)} />
            <Route path="/announcements" element={withSuspense(AnnouncementsPage, 900)} />
            <Route path="/announcements/:slug" element={withSuspense(AnnouncementDetail, 900)} />
            <Route path="/story/:id" element={withSuspense(StoryPage, 700)} />
            <Route path="/stories" element={withSuspense(AllStories, 900)} />
            <Route path="/terms" element={withSuspense(LegalPage, 700)} />
            <Route path="/sign/:token" element={withSuspense(SignDocumentPage, 700)} />
            <Route path="/verify" element={withSuspense(VerifyDocumentPage, 700)} />
            <Route path="/verify/:code" element={withSuspense(VerifyDocumentPage, 700)} />
            <Route path="/feedback/:token" element={withSuspense(FeedbackSurveyPage, 700)} />
            <Route path="/consent/:token" element={withSuspense(ConsentPage, 700)} />
            <Route path="/documents/:token" element={withSuspense(DocumentTrackerPage, 700)} />
            <Route path="/lessons/test/:token" element={withSuspense(LessonTestPage, 700)} />
            <Route path="/lessons/:token" element={withSuspense(LessonsPage, 700)} />
            <Route path="/visa-documents/:token" element={withSuspense(VisaDocumentsPage, 700)} />
            <Route path="/client-documents/:token" element={withSuspense(ClientDocumentsPage, 700)} />
            <Route path="/privacy" element={withSuspense(LegalPage, 700)} />
            <Route path="/job-search-support" element={withSuspense(JobSearchSupportForm, 900)} />
            <Route path="/work-abroad/how-we-work" element={withSuspense(WorkAbroadTrustPage, 700)} />
            <Route path="/work-abroad/check-an-offer" element={withSuspense(WorkAbroadTrustPage, 700)} />
            <Route path="/emergency-support" element={withSuspense(EmergencySupportForm, 900)} />
            <Route path="/alumni-network" element={withSuspense(AlumniSignupForm, 900)} />
            <Route path="/consultation" element={withSuspense(ConsultationBooking, 700)} />
            <Route path="/consultation/education" element={withSuspense(EducationConsultationForm, 900)} />
            <Route path="/consultation/tourist" element={withSuspense(TourismConsultationForm, 900)} />
            <Route path="/medical-tourism" element={withSuspense(MedicalTourism, 900)} />
            <Route path="/admin-portal/login" element={withSuspense(AdminLogin, 500)} />
            <Route
              path="/admin-portal"
              element={<ProtectedRoute>{withSuspense(AdminDashboard, 700)}</ProtectedRoute>}
            >
              <Route index element={<Navigate to="/admin-portal/general" replace />} />
              <Route path="general" element={withSuspense(GeneralSettings, 500)} />
              <Route path="home-hero" element={withSuspense(HomeHero, 500)} />
              <Route path="home-partners" element={withSuspense(HomePartners, 500)} />
              <Route path="about-stats" element={withSuspense(AboutStatsManager, 500)} />
              <Route path="about-page" element={withSuspense(AboutPageManager, 500)} />
              <Route path="home-success-stories" element={withSuspense(SuccessStoriesManager, 500)} />
              <Route path="home-counsellors" element={withSuspense(CounsellorsManager, 500)} />
              <Route path="home-admission-results" element={withSuspense(AdmissionResultsManager, 500)} />
              <Route path="blog" element={withSuspense(BlogManager, 500)} />
              <Route path="blog/categories" element={withSuspense(BlogCategoriesManager, 500)} />
              <Route path="blog/new" element={withSuspense(BlogPostForm, 600)} />
              <Route path="blog/edit/:slug" element={withSuspense(BlogPostForm, 600)} />
              <Route path="universities" element={withSuspense(UniversitiesManager, 500)} />
              <Route path="universities/new" element={withSuspense(UniversityForm, 600)} />
              <Route path="universities/edit/:id" element={withSuspense(UniversityForm, 600)} />
              <Route path="programs" element={withSuspense(ProgramsManager, 500)} />
              <Route path="programs/new" element={withSuspense(ProgramForm, 600)} />
              <Route path="programs/edit/:id" element={withSuspense(ProgramForm, 600)} />
              <Route path="countries" element={withSuspense(CountriesManager, 500)} />
              <Route path="countries/new" element={withSuspense(CountryForm, 600)} />
              <Route path="countries/edit/:slug" element={withSuspense(CountryForm, 600)} />
              <Route path="pricing" element={withSuspense(PricingManager, 500)} />
              <Route path="scholarships" element={withSuspense(ScholarshipsManager, 500)} />
              <Route path="seo" element={withSuspense(SEOManager, 500)} />
              <Route path="terms-privacy" element={withSuspense(TermsPrivacyManager, 500)} />
              <Route path="pricing/new" element={withSuspense(PricingPackageForm, 600)} />
              <Route path="pricing/edit/:id" element={withSuspense(PricingPackageForm, 600)} />
              <Route path="registrations" element={withSuspense(RegistrationsManager, 500)} />
              <Route path="announcements" element={withSuspense(AnnouncementsManager, 500)} />
              <Route path="announcements/new" element={withSuspense(AnnouncementForm, 700)} />
              <Route path="announcements/edit/:slug" element={withSuspense(AnnouncementForm, 700)} />
              <Route path="announcement-subscriptions" element={withSuspense(AnnouncementSubscriptionsManager, 500)} />
              <Route path="calendar" element={withSuspense(CalendarManager, 500)} />
              <Route path="job-search-inquiries" element={withSuspense(JobSearchInquiriesManager, 500)} />
              <Route path="emergency-support" element={withSuspense(EmergencySupportManager, 500)} />
              <Route path="alumni-signups" element={withSuspense(AlumniSignupsManager, 500)} />
              <Route path="education-consultations" element={withSuspense(EducationConsultationManager, 500)} />
              <Route path="tourism-consultations" element={withSuspense(TourismConsultationManager, 500)} />
            </Route>
            <Route path="/country/:slug" element={withSuspense(DynamicCountry, 1400)} />
            <Route path="/destinations" element={withSuspense(Destinations, 1400)} />
            {countryPages.countries.map((c) => (
              <Route
                key={c.slug}
                path={`${COUNTRY_PAGE_PREFIX}${c.slug}`}
                element={<Suspense fallback={<LoadingSpinner minHeight={1400} label="Loading..." />}><CountryPage slug={c.slug} /></Suspense>}
              />
            ))}
            <Route path="/become-an-agent" element={withSuspense(BecomeAnAgent, 900)} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
        </main>
        <Suspense fallback={null}>
          <ContactInfoModal show={contactModal.shouldShow} onClose={contactModal.dismiss} />
        </Suspense>
        <FloatingActions />
        <UpdateBanner
          registration={waitingRegistration}
          onDismiss={() => setWaitingRegistration(null)}
        />
        </SiteContentProvider>
      </Router>
    </MotionConfig>
  );
}

export default App;
